import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { 
  Clock, Check, X, AlertCircle, Building2, 
  ArrowRight, RefreshCw, CreditCard, ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import Navigation from '@/components/Navigation';
import api from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const StatusIcon = ({ status }) => {
  const config = {
    pending: { icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-100 dark:bg-yellow-900/30' },
    under_review: { icon: Clock, color: 'text-blue-600', bg: 'bg-blue-100 dark:bg-blue-900/30' },
    approved: { icon: Check, color: 'text-green-600', bg: 'bg-green-100 dark:bg-green-900/30' },
    rejected: { icon: X, color: 'text-red-600', bg: 'bg-red-100 dark:bg-red-900/30' },
    needs_info: { icon: AlertCircle, color: 'text-orange-600', bg: 'bg-orange-100 dark:bg-orange-900/30' },
  };
  
  const { icon: Icon, color, bg } = config[status] || config.pending;
  
  return (
    <div className={cn("w-20 h-20 rounded-full flex items-center justify-center", bg)}>
      <Icon className={cn("w-10 h-10", color)} />
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const styles = {
    pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    approved: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    under_review: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    needs_info: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  };
  
  const labels = {
    pending: 'Pending Review',
    approved: 'Approved',
    rejected: 'Rejected',
    under_review: 'Under Review',
    needs_info: 'More Info Needed',
  };
  
  return (
    <Badge className={styles[status] || styles.pending}>
      {labels[status] || status}
    </Badge>
  );
};

const ApplicationStatus = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeStatus, setStripeStatus] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    
    fetchApplication();
    
    // Check for Stripe redirect
    if (searchParams.get('success') === 'true') {
      toast.success('Stripe onboarding completed!');
      checkStripeStatus();
    } else if (searchParams.get('refresh') === 'true') {
      toast.info('Please complete Stripe onboarding');
    }
  }, [isAuthenticated, authLoading, searchParams]);

  const fetchApplication = async () => {
    try {
      const response = await api.get('/onboarding/application-status');
      if (response.data.application) {
        setApplication(response.data.application);
        if (response.data.status === 'approved') {
          checkStripeStatus();
        }
      }
    } catch (error) {
      console.error('Failed to fetch application:', error);
      toast.error('Failed to load application status');
    } finally {
      setLoading(false);
    }
  };

  const checkStripeStatus = async () => {
    try {
      const response = await api.get('/onboarding/stripe/account-status');
      setStripeStatus(response.data);
    } catch (error) {
      console.error('Failed to check Stripe status:', error);
    }
  };

  const createStripeAccount = async () => {
    setStripeLoading(true);
    try {
      // First create the account if needed
      if (!stripeStatus?.has_account) {
        await api.post('/onboarding/stripe/create-account');
      }
      
      // Get the onboarding link
      const baseUrl = window.location.origin;
      const response = await api.post('/onboarding/stripe/onboarding-link', null, {
        params: { return_url: `${baseUrl}/partner/application-status` }
      });
      
      // Redirect to Stripe
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start Stripe setup');
    } finally {
      setStripeLoading(false);
    }
  };

  const openStripeDashboard = async () => {
    try {
      const response = await api.get('/onboarding/stripe/dashboard-link');
      window.open(response.data.url, '_blank');
    } catch (error) {
      toast.error('Failed to open Stripe dashboard');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (!application) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="w-20 h-20 rounded-full bg-gray-100 dark:bg-gray-800 mx-auto mb-6 flex items-center justify-center">
            <Building2 className="w-10 h-10 text-gray-400" />
          </div>
          <h1 className="text-3xl font-bold mb-4">No Application Found</h1>
          <p className="text-muted-foreground mb-8">
            You haven&apos;t submitted a partner application yet.
          </p>
          <Button onClick={() => navigate('/partner/onboarding')}>
            Start Application
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Status Header */}
        <div className="text-center mb-8">
          <StatusIcon status={application.status} />
          <h1 className="text-3xl font-bold mt-6 mb-2">
            {application.status === 'approved' ? 'Congratulations!' :
             application.status === 'rejected' ? 'Application Update' :
             application.status === 'needs_info' ? 'Action Required' :
             'Application Submitted'}
          </h1>
          <StatusBadge status={application.status} />
        </div>

        {/* Application Details Card */}
        <Card className="border-0 shadow-lg mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl">{application.business_name}</CardTitle>
                <CardDescription>{application.venue_type}</CardDescription>
              </div>
              <Badge variant="outline">{application.price_level}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Address</p>
                <p className="font-medium">{application.address}</p>
                <p className="font-medium">{application.city}, {application.state} {application.zip_code}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Contact</p>
                <p className="font-medium">{application.phone}</p>
                <p className="font-medium">{application.email}</p>
              </div>
            </div>
            
            {application.business_description && (
              <div>
                <p className="text-muted-foreground text-sm mb-1">Description</p>
                <p className="text-sm">{application.business_description}</p>
              </div>
            )}
            
            {application.amenities?.length > 0 && (
              <div>
                <p className="text-muted-foreground text-sm mb-2">Amenities</p>
                <div className="flex flex-wrap gap-1">
                  {application.amenities.map(a => (
                    <Badge key={a} variant="secondary" className="text-xs">{a}</Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Status-Specific Content */}
        {application.status === 'pending' && (
          <Card className="border-0 shadow-md bg-yellow-50 dark:bg-yellow-950">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <Clock className="w-6 h-6 text-yellow-600 mt-1" />
                <div>
                  <h3 className="font-semibold text-yellow-800 dark:text-yellow-200">Under Review</h3>
                  <p className="text-yellow-700 dark:text-yellow-300 text-sm mt-1">
                    Our team is reviewing your application. You&apos;ll receive an email once it&apos;s processed,
                    typically within 2-3 business days.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {application.status === 'approved' && (
          <div className="space-y-4">
            <Card className="border-0 shadow-md bg-green-50 dark:bg-green-950">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <Check className="w-6 h-6 text-green-600 mt-1" />
                  <div>
                    <h3 className="font-semibold text-green-800 dark:text-green-200">Application Approved!</h3>
                    <p className="text-green-700 dark:text-green-300 text-sm mt-1">
                      Your venue is now live on EVENT★GOERS. Complete the steps below to start accepting payments.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stripe Setup Card */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Payment Setup
                </CardTitle>
                <CardDescription>
                  Connect with Stripe to receive payouts for bookings and tickets
                </CardDescription>
              </CardHeader>
              <CardContent>
                {stripeStatus?.onboarding_complete ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-green-600">
                      <Check className="w-5 h-5" />
                      <span className="font-medium">Stripe Connected</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Your Stripe account is fully set up. You can now receive payouts.
                    </p>
                    <Button variant="outline" onClick={openStripeDashboard}>
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Open Stripe Dashboard
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 bg-amber-50 dark:bg-amber-950 rounded-lg border border-amber-200 dark:border-amber-800">
                      <p className="text-amber-800 dark:text-amber-200 text-sm">
                        <strong>Commission Structure:</strong>
                      </p>
                      <ul className="text-amber-700 dark:text-amber-300 text-sm mt-2 space-y-1">
                        <li>• Event Tickets: 12% platform fee</li>
                        <li>• Experiences/Vouchers: 30% platform fee</li>
                        <li>• Private Hire Bookings: 15% platform fee</li>
                      </ul>
                    </div>
                    
                    <Button 
                      onClick={createStripeAccount} 
                      disabled={stripeLoading}
                      className="w-full"
                      data-testid="setup-stripe-btn"
                    >
                      {stripeLoading ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Setting up...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          {stripeStatus?.has_account ? 'Continue Stripe Setup' : 'Set Up Stripe Account'}
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Next Steps */}
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle className="text-lg">Next Steps</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3">
                    <div className={cn(
                      "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
                      stripeStatus?.onboarding_complete ? "bg-green-500 text-white" : "bg-gray-200 text-gray-600"
                    )}>
                      {stripeStatus?.onboarding_complete ? <Check className="w-4 h-4" /> : '1'}
                    </div>
                    <span className={stripeStatus?.onboarding_complete ? "line-through text-muted-foreground" : ""}>
                      Complete Stripe onboarding
                    </span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600">
                      2
                    </div>
                    <span>Add events and experiences to attract customers</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600">
                      3
                    </div>
                    <span>Start accepting reservations!</span>
                  </li>
                </ul>
                
                <div className="mt-6">
                  <Button onClick={() => navigate('/partner/dashboard')} className="w-full">
                    Go to Partner Dashboard
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {application.status === 'rejected' && (
          <Card className="border-0 shadow-md bg-red-50 dark:bg-red-950">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <X className="w-6 h-6 text-red-600 mt-1" />
                <div>
                  <h3 className="font-semibold text-red-800 dark:text-red-200">Application Not Approved</h3>
                  {application.rejection_reason && (
                    <p className="text-red-700 dark:text-red-300 text-sm mt-2">
                      <strong>Reason:</strong> {application.rejection_reason}
                    </p>
                  )}
                  <p className="text-red-700 dark:text-red-300 text-sm mt-2">
                    If you believe this was a mistake or would like to provide additional information,
                    please contact our support team.
                  </p>
                  <Button 
                    variant="outline" 
                    className="mt-4 border-red-300 text-red-700 hover:bg-red-100"
                    onClick={() => navigate('/partner/onboarding')}
                  >
                    Submit New Application
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {application.status === 'needs_info' && (
          <Card className="border-0 shadow-md bg-orange-50 dark:bg-orange-950">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-orange-600 mt-1" />
                <div>
                  <h3 className="font-semibold text-orange-800 dark:text-orange-200">Additional Information Needed</h3>
                  <p className="text-orange-700 dark:text-orange-300 text-sm mt-1">
                    Our team needs more information to process your application. 
                    Please check your email for details.
                  </p>
                  <Button 
                    variant="outline" 
                    className="mt-4 border-orange-300 text-orange-700 hover:bg-orange-100"
                    onClick={() => navigate('/partner/onboarding')}
                  >
                    Update Application
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Refresh Button */}
        <div className="text-center mt-6">
          <Button 
            variant="ghost" 
            onClick={() => {
              setLoading(true);
              fetchApplication();
            }}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh Status
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ApplicationStatus;
